// src/components/FeedCard.jsx
import React from "react";
import {
Box,
  Card,
  CardMedia,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  TextField,
  Button,
  Snackbar,
   Tooltip, 
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

/* -----------------------------
   Time helper (human, calm)
------------------------------ */
function expiresIn(dateString) {
  const posted = new Date(dateString);
  const expiresMs = posted.getTime() + 24 * 60 * 60 * 1000;
  const diffMs = expiresMs - Date.now();

  // already expired (shouldn't show, but safe)
  if (diffMs <= 0) return "Expired";

  const totalMins = Math.ceil(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;

  if (hours <= 0) return `Expires in ${mins}m`;
  return `Expires in ${hours}h ${mins}m`;
}


export default function FeedCard({ item, onDelete, onOpen, fullscreen }) {


  const navigate = useNavigate();

  // ✅ ownership logic (CORRECT PLACE)
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isOwnPost = currentUser?.id === item.userId;

  const [anchorEl, setAnchorEl] = React.useState(null);
const open = Boolean(anchorEl);

const handleOpenMenu = (e) => {
  e.stopPropagation();
  setAnchorEl(e.currentTarget);
};

const handleCloseMenu = () => {
  setAnchorEl(null);
};

const [editOpen, setEditOpen] = React.useState(false);
const [captionDraft, setCaptionDraft] = React.useState(item.caption || "");
const [inviteOpen, setInviteOpen] = React.useState(false);
const [inviteNote, setInviteNote] = React.useState("");
const [inviteSent, setInviteSent] = React.useState(false);
const [invitePending, setInvitePending] = React.useState(null);
const [inviteBlockedMsg, setInviteBlockedMsg] = React.useState("");

const [inviteDirection, setInviteDirection] = React.useState(null);
// "sent" | "received" | null






  const avatarSrc = item.profilePic
    ? item.profilePic.startsWith("http")
      ? item.profilePic
      : `${API_BASE}${item.profilePic}`
    : undefined;

  const mediaSrc = item.mediaUrl
    ? `${API_BASE}${item.mediaUrl}`
    : null;

    React.useEffect(() => {
  if (!currentUser || isOwnPost) return;

  let timeoutId;

  const checkInvite = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/invites/status?userA=${currentUser.id}&userB=${item.userId}`

      );

      const data = await res.json();

      setInvitePending(data.pending);

if (data.pending) {
  // guarantee sender is never misclassified
  setInviteDirection(data.direction || "sent");
} else {
  setInviteDirection(null);
}


      // Clear any previous timer
      if (timeoutId) clearTimeout(timeoutId);

      // Auto-reset after expiry OR "Not now"
      if (data.pending && data.expiresAt) {
        const msUntilExpiry =
          new Date(data.expiresAt).getTime() - Date.now();

        if (msUntilExpiry > 0) {
          timeoutId = setTimeout(() => {
            setInvitePending(false);
          }, msUntilExpiry);
        }
      }
    } catch (err) {
      console.error("Failed to check invite status", err);
    }
  };

  checkInvite(); // ✅ correct placement

  return () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
}, [currentUser?.id, item.userId, isOwnPost]);





  return (
    // ✅ scroll anchor
    <Box id={`post-${item.id}`}>
      <Card
  onClick={() => onOpen?.()}
  sx={{
    mb: 3,
    p: 2,
    borderRadius: 3,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    cursor: onOpen ? "pointer" : "default",
    ...(fullscreen ? { maxWidth: 820, mx: "auto" } : {}),
  }}
>

        {/* ================= HEADER ================= */}
        {/* ================= HEADER ================= */}
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    mb: 1.25,
  }}
>
  {/* 👤 Clickable profile area ONLY */}
  <Box
    onClick={(e) => {
      e.stopPropagation();
      navigate(`/profile/${item.userId}`);
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      cursor: "pointer",
    }}
  >
    <Avatar
      src={avatarSrc}
      sx={{ width: 36, height: 36, bgcolor: "#E5E7EB" }}
    />

    <Typography
      sx={{
        fontWeight: 600,
        fontSize: "0.95rem",
        color: "text.primary",
        lineHeight: 1.2,
      }}
    >
      {item.username}
    </Typography>
  </Box>

  {/* ℹ️ Meta info (NOT clickable) */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
      Nearby · {expiresIn(item.createdAt)}
    </Typography>

    {item.courseCode && (
      <Tooltip title={item.courseName || item.courseCode} arrow>
        <Box
          sx={{
            px: 1,
            py: "2px",
            borderRadius: "999px",
            background: "rgba(34, 197, 94, 0.14)",
            color: "#16A34A",
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {item.courseCode}
        </Box>
      </Tooltip>
    )}
  </Box>
</Box>


        {/* ================= CAPTION ================= */}
        {item.caption && (
          <Typography
            sx={{
              mb: 1.25,
              fontSize: "0.95rem",
              color: "text.primary",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {item.caption}
          </Typography>
        )}

        {/* ================= MEDIA ================= */}
        {mediaSrc && (
          <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
            <CardMedia
              component={item.mediaType === "video" ? "video" : "img"}
              src={mediaSrc}
              controls={item.mediaType === "video"}
              onClick={(e) => {
                // only block clicks on VIDEO controls
                if (item.mediaType === "video") e.stopPropagation();
              }}
              sx={{
                width: "100%",
                borderRadius: 2,
                objectFit: "cover",
                maxHeight: fullscreen ? "85vh" : 420,
                backgroundColor: "#000",
              }}
            />
          </Box>
        )}



        {/* ================= ACTIONS ================= */}
        <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
          {/* Invite — always visible */}
          {!isOwnPost && (
          <Box
   onClick={async (e) => {
    e.stopPropagation();

    // ⛔ still checking
    // ⛔ still checking
if (invitePending === null) return;

// 📩 receiver → go to notifications
if (invitePending && inviteDirection === "received") {
  window.dispatchEvent(new Event("open-notifications"));
  return;
}

// ⏳ ANY pending invite (sender or receiver) blocks invite dialog
if (invitePending) return;
// ✅ BLOCK if YOU already have an active moment
    try {
      const r = await fetch(`${API_BASE}/moments/active/${currentUser.id}`);
      const d = await r.json();
      if (d?.momentId) {
        setInviteBlockedMsg("You’re already in a moment. End it before inviting someone.");
        return;
      }
    } catch {
      // if check fails, allow (or block—your choice)
    }

// ⚡ no invite → open dialog
setInviteOpen(true);

  }}

  sx={{
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 2,
    py: 0.75,
    borderRadius: "999px",
    backgroundColor:
      invitePending === null
        ? "#E5E7EB"   // neutral while syncing
        : invitePending
        ? "#F59E0B"   // pending
        : "#16A34A",  // invite
    color: "#FFFFFF",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor:
  invitePending && inviteDirection === "received"
    ? "pointer"
    : invitePending
    ? "default"
    : "pointer",

    opacity: invitePending === null ? 0.7 : invitePending ? 0.9 : 1,
    userSelect: "none",
  }}
>
{invitePending === null
  ? "⏳ Checking"
  : invitePending && inviteDirection === "sent"
  ? "⏳ Pending"
  : invitePending && inviteDirection === "received"
  ? "📩 Invitation received"
  : "⚡ Invite"}

</Box>
)}

          {/* Edit — ONLY for your own posts */}
          {isOwnPost && (
  <>
    <Box
      onClick={handleOpenMenu}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.75,
        py: 0.65,
        borderRadius: "999px",
        border: "1.5px solid #F59E0B",
        color: "#92400E",
        backgroundColor: "transparent",
        fontSize: "0.8rem",
        fontWeight: 600,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
          backgroundColor: "#FEF3C7",
        },
        "&:active": {
          transform: "scale(0.97)",
        },
      }}
    >
      ✏️ Edit
    </Box>

    {/* ===== Edit Menu ===== */}
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleCloseMenu}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        onClick: (e) => e.stopPropagation(),
        sx: {
          borderRadius: 2,
          mt: 0.5,
          minWidth: 180,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      
        <MenuItem
  onClick={() => {
    handleCloseMenu();
    setCaptionDraft(item.caption || "");
    setEditOpen(true);
  }}
  sx={{ fontSize: "0.9rem", fontWeight: 500 }}
>
  ✏️ Rewrite caption
</MenuItem>


    <MenuItem
  onClick={async () => {
    handleCloseMenu();

    try {
      const res = await fetch(`${API_BASE}/posts/${item.id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        throw new Error("Failed to delete post");
      }

      // ✅ Tell React to remove it
      onDelete(item.id);

// 🔔 Notify Pulse system
window.dispatchEvent(
  new CustomEvent("post-deleted", {
    detail: { postId: item.id },
  })
);

    } catch (err) {
      console.error("Failed to delete post", err);
    }
  }}
  sx={{
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#DC2626",
  }}
>
  🗑 Remove
</MenuItem>


    </Menu>
  </>
)}

        </Box>
        {/* ===== Rewrite Caption Snackbar ===== */}
<Dialog
  open={editOpen}
  onClose={() => setEditOpen(false)}
  fullWidth
  maxWidth="xs"
  onClick={(e) => e.stopPropagation()}   // ✅ ADD THIS
>
  <DialogContent
   onClick={(e) => e.stopPropagation()} // ✅ ADD THIS
    sx={{
      p: 3,
      borderRadius: 3,
    }}
  >
    <Typography
      sx={{
        fontWeight: 600,
        mb: 1.5,
        fontSize: "1rem",
        textAlign: "center",
      }}
    >
      Rewrite your moment
    </Typography>

    <TextField
      fullWidth
      multiline
      minRows={3}
      maxRows={6}
      value={captionDraft}
      onChange={(e) => setCaptionDraft(e.target.value)}
      placeholder="What’s happening?"
      sx={{ mb: 2 }}
    />

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Button
        onClick={() => setEditOpen(false)}
        sx={{ color: "#16A34A", fontWeight: 600 }}
      >
        Cancel
      </Button>

      <Button
        variant="contained"
        color="primary"
        onClick={async () => {
          try {
            await fetch(`${API_BASE}/posts/${item.id}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ caption: captionDraft }),
            });

            item.caption = captionDraft;
            setEditOpen(false);
          } catch (err) {
            console.error("Failed to update caption", err);
          }
        }}
      >
        Apply
      </Button>
    </Box>
  </DialogContent>
</Dialog>

{/* ===== Invite Dialog ===== */}
<Dialog
  open={inviteOpen}
  onClose={() => setInviteOpen(false)}
  fullWidth
  maxWidth="xs"
    onClick={(e) => e.stopPropagation()}   // ✅ ADD THIS
>
  <DialogContent
   onClick={(e) => e.stopPropagation()} // ✅ ADD THIS
    sx={{
      p: 3,
      borderRadius: 3,
    }}
  >
    <Typography
      sx={{
        fontWeight: 600,
        mb: 1.5,
        fontSize: "1rem",
        textAlign: "center",
      }}
    >
      Open a moment
    </Typography>

    <TextField
      fullWidth
      multiline
      minRows={2}
      maxRows={4}
      value={inviteNote}
      onChange={(e) => setInviteNote(e.target.value)}
      placeholder="Why you’re inviting (optional)"
      sx={{ mb: 2 }}
    />

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Button
        onClick={() => {
          setInviteNote("");
          setInviteOpen(false);
        }}
        sx={{ color: "#16A34A", fontWeight: 600 }}
      >
        Cancel
      </Button>

      <Button
  variant="contained"
  onClick={async () => {
  try {
    const resp = await fetch(`${API_BASE}/invites`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fromUserId: currentUser.id,
        toUserId: item.userId,
        note: inviteNote,
      }),
    });

    const data = await resp.json().catch(() => ({}));

    if (resp.status === 409) {
      // ✅ backend blocked because someone is in a moment
      setInviteBlockedMsg(
        data?.code === "USER_ALREADY_IN_MOMENT"
          ? "They’re already in a moment right now."
          : "You’re already in a moment. End it before inviting someone."
      );
      return;
    }

    if (!resp.ok) {
      throw new Error(data?.error || "Invite failed");
    }

    setInvitePending(true);
    setInviteSent(true);
    setInviteNote("");
    setInviteOpen(false);
  } catch (err) {
    console.error("Failed to send invite", err);
    setInviteBlockedMsg("Could not send invite. Try again.");
  }
}}

>
  Send
</Button>



    </Box>
  </DialogContent>
</Dialog>

<Snackbar
  open={inviteSent}
  autoHideDuration={4000}
  onClose={() => setInviteSent(false)}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert
    onClose={() => setInviteSent(false)}
    severity="success"
    sx={{
      borderRadius: 2,
      fontWeight: 500,
    }}
  >
    Sent · This invitation will close automatically after 30 minutes if there’s no response
  </Alert>
</Snackbar>

<Snackbar
  open={!!inviteBlockedMsg}
  autoHideDuration={3500}
  onClose={() => setInviteBlockedMsg("")}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert
    onClose={() => setInviteBlockedMsg("")}
    severity="warning"
    sx={{ borderRadius: 2, fontWeight: 600 }}
  >
    {inviteBlockedMsg}
  </Alert>
</Snackbar>





      </Card>
    </Box>
  );
}
